import React, { useState, useMemo } from 'react';
import { useGetRecapMatieresAssocieesQuery } from '../../services/copiesApi';
import ScrollToTop from '../../components/ui/ScrollToTop';
import {
  FileText, Search, Filter, Printer, Download, RefreshCw,
  Users, BookOpen, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2
} from 'lucide-react';

const RecapMatieresAssociees: React.FC = () => {
  const [filtreNiveau, setFiltreNiveau] = useState('');
  const [filtrePole, setFiltrePole] = useState('');
  const [filtreOption, setFiltreOption] = useState('');
  const [recherche, setRecherche] = useState('');
  const [groupesReduits, setGroupesReduits] = useState<Set<string>>(new Set());

  const { data, isLoading, refetch } = useGetRecapMatieresAssocieesQuery({
    niveau: filtreNiveau || undefined,
    pole: filtrePole || undefined,
    option: filtreOption || undefined,
  });

  const matieresFiltrees = useMemo(() => {
    if (!data?.matieres) return [];
    if (!recherche) return data.matieres;
    const term = recherche.toLowerCase();
    return data.matieres.filter(m =>
      m.matiere_nom.toLowerCase().includes(term) ||
      m.matiere_code.toLowerCase().includes(term) ||
      m.classes_label.toLowerCase().includes(term) ||
      m.enseignant_label.toLowerCase().includes(term) ||
      m.utilisateur_label.toLowerCase().includes(term)
    );
  }, [data, recherche]);

  // Grouper par niveau
  const groupes = useMemo(() => {
    const map: Record<string, typeof matieresFiltrees> = {};
    matieresFiltrees.forEach(m => {
      const key = m.niveau;
      if (!map[key]) map[key] = [];
      map[key].push(m);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [matieresFiltrees]);

  const toggleGroupe = (key: string) => {
    setGroupesReduits(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const nivColors: Record<string, string> = {
    L1: 'bg-blue-600', L2: 'bg-green-600', L3: 'bg-purple-600',
    M1: 'bg-orange-600', M2: 'bg-red-600',
  };

  const handleImprimer = () => {
    const html = `
      <html><head><title>Recap Matieres Associees</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; }
        h1 { font-size: 16px; text-align: center; margin-bottom: 5px; }
        h2 { font-size: 13px; color: #1e40af; margin: 15px 0 5px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
        th { background: #1e3a5f; color: white; padding: 6px 8px; text-align: left; font-size: 10px; }
        td { border: 1px solid #ddd; padding: 5px 8px; font-size: 10px; }
        tr:nth-child(even) { background: #f9f9f9; }
        .badge { display: inline-block; padding: 1px 6px; border-radius: 8px; font-size: 9px; font-weight: bold; }
        .total { font-weight: bold; text-align: right; padding: 8px; font-size: 12px; }
        @media print { body { margin: 10mm; } }
      </style></head><body>
      <h1>Recapitulatif des Matieres Associees</h1>
      <p style="text-align:center;color:#666;font-size:10px;">
        ${data?.total_matieres || 0} matieres - ${data?.total_copies || 0} copies
        ${filtreNiveau ? ` | Niveau: ${filtreNiveau}` : ''}
        ${filtrePole ? ` | Pole: ${filtrePole}` : ''}
      </p>
      <table>
        <thead><tr>
          <th>N</th><th>Code</th><th>Matiere</th><th>Niveau</th>
          <th>Classes</th><th>Formateur</th><th>Copies</th><th>Saisi par</th>
        </tr></thead>
        <tbody>
        ${matieresFiltrees.map((m, i) => `
          <tr>
            <td>${i + 1}</td>
            <td style="font-family:monospace;font-size:9px;">${m.matiere_code}</td>
            <td><strong>${m.matiere_nom}</strong></td>
            <td><span class="badge" style="background:#e0e7ff;color:#3730a3;">${m.niveau}</span></td>
            <td>${m.classes.map(c => `${c.classe} (${c.count})`).join(', ')}</td>
            <td>${m.enseignant_label}</td>
            <td style="text-align:center;font-weight:bold;">${m.total_copies}</td>
            <td style="font-size:9px;">${m.utilisateur_label}</td>
          </tr>
        `).join('')}
        </tbody>
      </table>
      <div class="total">Total: ${matieresFiltrees.reduce((s, m) => s + m.total_copies, 0)} copies</div>
      </body></html>
    `;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.print(); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
            <BookOpen size={28} />
            Recapitulatif des Matieres Associees
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Vue d'ensemble des matieres, classes, formateurs et copies associees
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleImprimer} className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Printer size={16} className="mr-2" /> Imprimer
          </button>
          <button onClick={() => refetch()} className="flex items-center px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600">
            <RefreshCw size={16} className="mr-2" /> Actualiser
          </button>
        </div>
      </div>

      {/* Stats */}
      {data && (
        <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Matieres</div>
            <div className="text-3xl font-extrabold text-gray-900 dark:text-gray-100 mt-1">{data.total_matieres}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Copies associees</div>
            <div className="text-3xl font-extrabold text-blue-600 dark:text-blue-400 mt-1">{data.total_copies}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Niveaux</div>
            <div className="flex gap-1 mt-2">
              {data.niveaux.map(n => (
                <span key={n} className={`text-xs font-bold text-white px-2 py-0.5 rounded ${nivColors[n] || 'bg-gray-500'}`}>{n}</span>
              ))}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Poles</div>
            <div className="flex flex-wrap gap-1 mt-2">
              {data.poles.map(p => (
                <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">{p}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="mb-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={16} className="text-gray-500" />
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Filtres</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Recherche</label>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                placeholder="Matiere, code, formateur..."
                className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Niveau</label>
            <select value={filtreNiveau} onChange={(e) => setFiltreNiveau(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
              <option value="">Tous</option>
              {(data?.niveaux || []).map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Pole</label>
            <select value={filtrePole} onChange={(e) => setFiltrePole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
              <option value="">Tous</option>
              {(data?.poles || []).map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Option</label>
            <select value={filtreOption} onChange={(e) => setFiltreOption(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
              <option value="">Toutes</option>
              {(data?.options || []).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
            Chargement...
          </div>
        ) : matieresFiltrees.length === 0 ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            <FileText size={32} className="mx-auto mb-2 opacity-50" />
            Aucune matiere associee trouvee
          </div>
        ) : (
          <>
            {groupes.map(([niveau, matieres]) => {
              const isReduit = groupesReduits.has(niveau);
              const totalCopiesGroupe = matieres.reduce((s, m) => s + m.total_copies, 0);
              return (
                <div key={niveau}>
                  {/* Header groupe */}
                  <div
                    onClick={() => toggleGroupe(niveau)}
                    className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <div className="flex items-center gap-3">
                      {isReduit ? <ChevronRight size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                      <span className={`text-xs font-bold text-white px-2.5 py-1 rounded ${nivColors[niveau] || 'bg-gray-500'}`}>
                        {niveau}
                      </span>
                      <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                        {matieres.length} matiere(s)
                      </span>
                    </div>
                    <span className="text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2.5 py-1 rounded-full">
                      {totalCopiesGroupe} copies
                    </span>
                  </div>

                  {/* Table du groupe */}
                  {!isReduit && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-gray-700">
                            <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase w-8">N</th>
                            <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Code</th>
                            <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Matiere</th>
                            <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Classes</th>
                            <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Formateur</th>
                            <th className="px-3 py-2 text-center text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Copies</th>
                            <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Saisi par</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                          {matieres.map((m, idx) => (
                            <tr key={m.matiere_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                              <td className="px-3 py-2.5 text-xs text-gray-400">{idx + 1}</td>
                              <td className="px-3 py-2.5">
                                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                                  {m.matiere_code}
                                </span>
                              </td>
                              <td className="px-3 py-2.5">
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{m.matiere_nom}</span>
                                <div className="text-[10px] text-gray-400 mt-0.5">S{m.semestre} {m.option && `| ${m.option}`}</div>
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="flex flex-wrap gap-1">
                                  {m.classes.map(c => (
                                    <span key={c.classe} className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                                      {c.classe} <strong className="ml-1">{c.count}</strong>
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td className="px-3 py-2.5">
                                {m.enseignants.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {m.enseignants.map(e => (
                                      <span key={e.id} className="text-xs text-green-700 dark:text-green-400 font-medium">
                                        {e.nom}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="flex items-center gap-1 text-xs text-orange-500">
                                    <AlertTriangle size={12} /> Non attribue
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-center">
                                <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{m.total_copies}</span>
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="flex flex-wrap gap-1">
                                  {m.utilisateurs.map(u => (
                                    <span key={u.nom} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                                      {u.nom} ({u.count})
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Footer total */}
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {matieresFiltrees.length} matiere(s) affichee(s)
              </span>
              <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                Total : {matieresFiltrees.reduce((s, m) => s + m.total_copies, 0)} copies
              </span>
            </div>
          </>
        )}
      </div>

      {/* Bouton flottant « Retour en haut » */}
      <ScrollToTop threshold={500} />
    </div>
  );
};

export default RecapMatieresAssociees;
